import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { CommonService } from 'src/app/services/common.service';
import { TenantSwitchService } from 'src/app/services/tenant-switch.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import {
  TENANT_CAPABILITY_API_MANAGE_SERVICE_NUMBER_AUTHORIZATION,
  resolveCapabilityAppCodesFromApiCodes,
  TENANT_CAPABILITY_MANAGE_SERVICE_NUMBER_AUTHORIZATION,
  TENANT_CAPABILITY_DEVICE_NETWORK_ACCESS_CONTROL,
  TENANT_CAPABILITY_API_DEVICE_NETWORK_ACCESS_CONTROL,
  TENANT_CAPABILITY_CLIENT_ACCESS_CONTROL,
  TENANT_CAPABILITY_API_CLIENT_ACCESS_CONTROL
} from 'src/app/shared/utils/tenant-capability.util';
import { ConstantUtil } from 'src/app/util/constant.util';

interface CapabilityMeta {
  code: string;
  apiCode: string;
  titleKey: string;
  descriptionKey: string;
}

interface TenantCapabilityOption {
  code: string;
  description?: string;
}

interface TenantCapabilityConfig {
  tenantId?: string;
  capabilities?: string[];
  options?: TenantCapabilityOption[];
}

@Component({
  selector: 'tenant-capability-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzCardModule,
    NzEmptyModule,
    NzFormModule,
    NzIconModule,
    NzSpinModule,
    NzSwitchModule,
    NzTagModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './tenant-capability-config.component.html',
  styleUrls: ['./tenant-capability-config.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantCapabilityConfigComponent implements OnInit {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly message = inject(NzMessageService);
  private readonly commonService = inject(CommonService);
  private readonly tenantSwitchService = inject(TenantSwitchService, { optional: true });

  @Input() tenantId = '';
  @Output() readonly capabilitiesChanged = new EventEmitter<string[]>();

  tenantCapabilityConfig: TenantCapabilityConfig | null = null;
  capabilityForm: FormGroup = this.fb.group({});
  loading = false;
  saving = false;
  private readonly controlNameMap = new Map<string, string>();

  private readonly capabilityMetas: CapabilityMeta[] = [
    {
      code: TENANT_CAPABILITY_MANAGE_SERVICE_NUMBER_AUTHORIZATION,
      apiCode: TENANT_CAPABILITY_API_MANAGE_SERVICE_NUMBER_AUTHORIZATION,
      titleKey: 'tenant.detail.capability.manage-service-number-authorization.title',
      descriptionKey: 'tenant.detail.capability.manage-service-number-authorization.description'
    },
    {
      code: TENANT_CAPABILITY_DEVICE_NETWORK_ACCESS_CONTROL,
      apiCode: TENANT_CAPABILITY_API_DEVICE_NETWORK_ACCESS_CONTROL,
      titleKey: 'tenant.detail.capability.device-network-access-control.title',
      descriptionKey: 'tenant.detail.capability.device-network-access-control.description'
    }
  ];

  ngOnInit(): void {
    this.loadCapabilityConfig();
  }

  get capabilityParams(): any[] {
    const optionCodes = new Set([
      ...(this.tenantCapabilityConfig?.options ?? []).map(option => String(option?.code ?? '').trim()),
      ...(this.tenantCapabilityConfig?.capabilities ?? []).map(code => String(code ?? '').trim())
    ]);

    return this.capabilityMetas.filter(meta => {
      if (optionCodes.has(meta.apiCode)) {
        return true;
      }
      if (
        (meta.apiCode === TENANT_CAPABILITY_API_DEVICE_NETWORK_ACCESS_CONTROL || meta.apiCode === 'ClientAccessControl') &&
        (optionCodes.has('DeviceNetworkAccessControl') || optionCodes.has('ClientAccessControl') || optionCodes.has('PublicIpRangeManage'))
      ) {
        return true;
      }
      return false;
    });
  }

  get hasCapabilities(): boolean {
    return this.capabilityParams.length > 0;
  }

  get enabledCapabilityCount(): number {
    return this.capabilityParams.filter(param => this.isCapabilityEnabled(param.code)).length;
  }

  loadCapabilityConfig(): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.TENANT_CAPABILITY_V1_ITEM}/${this.tenantId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success) {
          this.tenantCapabilityConfig = res.data ?? null;
          this.initCapabilityForm();
        } else {
          this.message.error(this.i18n.fanyi('tenant.config.load-failed'));
        }
        this.cdr.detectChanges();
      });
  }

  initCapabilityForm(): void {
    this.capabilityForm = this.fb.group({});
    this.controlNameMap.clear();
    const enabledApiCodes = new Set((this.tenantCapabilityConfig?.capabilities ?? []).map(code => String(code ?? '').trim()));

    this.capabilityParams.forEach((param: CapabilityMeta) => {
      const controlName = this.getControlName(param.code);
      const isEnabled =
        enabledApiCodes.has(param.apiCode) ||
        ((param.apiCode === TENANT_CAPABILITY_API_DEVICE_NETWORK_ACCESS_CONTROL || param.apiCode === 'ClientAccessControl') &&
          (enabledApiCodes.has('DeviceNetworkAccessControl') ||
            enabledApiCodes.has('ClientAccessControl') ||
            enabledApiCodes.has('PublicIpRangeManage')));
      this.capabilityForm.addControl(
        controlName,
        this.fb.control({
          value: isEnabled,
          disabled: false
        })
      );
    });
  }

  saveCapabilityConfig(): void {
    if (this.capabilityForm.invalid || !this.tenantId) {
      Object.values(this.capabilityForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      this.message.warning(this.i18n.fanyi('tenant.config.complete_required_fields'));
      return;
    }

    const requestData = {
      tenantId: this.tenantId,
      capabilities: this.capabilityParams
        .filter((param: CapabilityMeta) => this.isCapabilityEnabled(param.code))
        .map((param: CapabilityMeta) => param.apiCode)
    };

    this.saving = true;
    this.http
      .post(ConstantUtil.TENANT_CAPABILITY_V1_UPDATE, requestData)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success) {
          this.tenantCapabilityConfig = res.data ?? {
            ...this.tenantCapabilityConfig,
            tenantId: this.tenantId,
            capabilities: requestData.capabilities
          };
          this.syncCurrentTenantCapabilityCache(resolveCapabilityAppCodesFromApiCodes(this.tenantCapabilityConfig?.capabilities ?? []));
          this.capabilitiesChanged.emit(this.tenantCapabilityConfig?.capabilities ?? []);
          this.message.success(this.i18n.fanyi('tenant.detail.capability.save-success'));
          this.loadCapabilityConfig();
        } else {
          this.message.error(this.i18n.fanyi('tenant.detail.capability.save-failed'));
        }
      });
  }

  resetCapabilityConfig(): void {
    const enabledApiCodes = new Set((this.tenantCapabilityConfig?.capabilities ?? []).map(code => String(code ?? '').trim()));
    this.capabilityParams.forEach((param: CapabilityMeta) => {
      const controlName = this.getControlName(param.code);
      const control = this.capabilityForm.get(controlName);
      if (control) {
        control.setValue(enabledApiCodes.has(param.apiCode));
        control.markAsPristine();
      }
    });
    this.message.info(this.i18n.fanyi('tenant.config.reset_to_current_value'));
  }

  getCapabilityTitle(code: string): string {
    const meta = this.getCapabilityMeta(code);
    const fallbackTitle = this.toReadableCapabilityLabel(meta?.code ?? meta?.apiCode ?? code);
    return this.resolveTranslatedText(meta?.titleKey ?? code, fallbackTitle);
  }

  getCapabilityDescription(code: string): string {
    const meta = this.getCapabilityMeta(code);
    const fallbackDescription = this.getCapabilityOptionDescription(meta?.apiCode ?? code);
    return this.resolveTranslatedText(meta?.descriptionKey ?? code, fallbackDescription);
  }

  isCapabilityEnabled(code: string): boolean {
    return this.capabilityForm.get(this.getControlName(code))?.value === true;
  }

  private syncCurrentTenantCapabilityCache(capabilityCodes: string[]): void {
    const currentTenant = this.commonService.getCurrentTenant();
    if (!currentTenant || String(currentTenant.id ?? '') !== String(this.tenantId ?? '')) {
      return;
    }
    const updatedTenant = {
      ...currentTenant,
      capabilityCodes
    };
    this.commonService.cacheTenantInfo(updatedTenant);
    this.tenantSwitchService?.updateMenus(updatedTenant);
  }

  private getCapabilityMeta(code: string | null | undefined): CapabilityMeta | null {
    const capabilityCode = String(code ?? '').trim();
    return this.capabilityMetas.find(item => item.code === capabilityCode || item.apiCode === capabilityCode) ?? null;
  }

  getCapabilityControlName(code: string): string {
    return this.getControlName(code);
  }

  private getCapabilityOptionDescription(code: string): string {
    const capabilityCode = String(code ?? '').trim();
    return (
      this.tenantCapabilityConfig?.options?.find(option => String(option?.code ?? '').trim() === capabilityCode)?.description?.trim() ?? ''
    );
  }

  private resolveTranslatedText(key: string, fallbackText = ''): string {
    const translated = String(this.i18n.fanyi(key) ?? '').trim();
    if (translated && translated !== key) {
      return translated;
    }
    return fallbackText || key;
  }

  private toReadableCapabilityLabel(code: string): string {
    const normalizedCode = String(code ?? '')
      .trim()
      .split('.')
      .pop()
      ?.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!normalizedCode) {
      return String(code ?? '').trim();
    }

    return normalizedCode
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private getControlName(code: string): string {
    const capabilityCode = String(code ?? '').trim();
    if (!this.controlNameMap.has(capabilityCode)) {
      this.controlNameMap.set(capabilityCode, capabilityCode.replace(/[^a-zA-Z0-9_-]/g, '__'));
    }
    return this.controlNameMap.get(capabilityCode) ?? capabilityCode;
  }
}
